import { useEffect, useRef, useState } from "react";
import { FOCUSABLE } from "./focusable";

export const useKeyboardScrollable = <T extends HTMLElement>(
  enabled = true,
  { evenWithFocusableContent = false }: { evenWithFocusableContent?: boolean } = {},
) => {
  const ref = useRef<T>(null);
  const [needsTabStop, setNeedsTabStop] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) {
      setNeedsTabStop(false);
      return;
    }
    const measure = () =>
      setNeedsTabStop(overflows(element) && (evenWithFocusableContent || element.querySelector(FOCUSABLE) === null));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const resizes = new ResizeObserver(measure);
    resizes.observe(element);
    Array.from(element.children).forEach((child) => resizes.observe(child));
    const mutations = new MutationObserver(measure);
    mutations.observe(element, { childList: true, subtree: true });
    return () => {
      resizes.disconnect();
      mutations.disconnect();
    };
  }, [enabled, evenWithFocusableContent]);

  return { ref, tabIndex: needsTabStop ? 0 : undefined };
};

const overflows = (element: HTMLElement): boolean =>
  element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
